import { LanguageEnumToLocale } from "@marble/core/modules/languages/constants";
import { DEFAULT_LOCALE } from "@marble/core/modules/timezones/format/constants";
import type { FormatApptDateOptions } from "@marble/core/modules/timezones/format/types";
import { Language } from "@prisma/client";
import { DateTime } from "luxon";

// Every formatter here takes an explicit `timezone`, with no default. There
// used to be one (Eastern), and it was the single biggest source of
// wrong-looking times in the app: omitting the argument rendered Eastern for
// everyone rather than the reader's own zone, and nothing at the call site
// said so. Deciding the zone is now the caller's job, because the caller is
// the only one who knows whose time it is.

/**
 * Formats an appointment date to include the date and time.
 *
 * If long is true, the full weekday name is included.
 *
 * Example with long=false:
 *  "Wed, Sep 15 at 3:00 PM"
 *
 * Example with long=true:
 *  "Wednesday, September 15 at 3:00 PM"
 *
 * Example with includeYear=true:
 *  "Wed, Sep 15, 2025 at 3:00 PM"
 */
export const formatApptDateTime = (
  date: Date,
  opts: FormatApptDateOptions,
): string => {
  const {
    long = false,
    timezone,
    language = Language.ENGLISH,
    includeYear = false,
    showTimezone = true,
  } = opts;

  return date.toLocaleString(_localeFor(language), {
    timeZone: timezone,
    weekday: long ? "long" : "short",
    month: "long",
    day: "numeric",
    year: includeYear ? "numeric" : undefined,
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    timeZoneName: showTimezone ? "short" : undefined,
  });
};

/** Formats a date for appointment display with weekday, month, and day. */
export const formatApptDate = (
  date: Date,
  opts: FormatApptDateOptions,
): string => {
  const {
    long = false,
    timezone,
    language = Language.ENGLISH,
    includeYear = false,
    showTimezone = true,
  } = opts;

  const formatted = date.toLocaleString(_localeFor(language), {
    timeZone: timezone,
    weekday: long ? "long" : "short",
    month: "long",
    day: "numeric",
    year: includeYear ? "numeric" : undefined,
  });

  // A date-only render still needs its zone where it sits beside times in
  // another one: which calendar day an instant falls on is itself
  // zone-dependent. The label is appended rather than asked of Intl, which
  // renders `timeZoneName` into the empty time slot and produces
  // "Mon, June 15 at EDT".
  return showTimezone
    ? `${formatted} (${getShortTimezoneLabel(date, timezone)})`
    : formatted;
};

export const formatApptDateWithEnd = (
  startDate: Date,
  endDate: Date,
  opts: {
    includeYear?: boolean;
    timezone: string;
    showTimezone?: boolean;
  },
): string => {
  const { includeYear = false, timezone, showTimezone = true } = opts;
  // The label belongs on the end of the range, not both halves, so the start
  // is rendered without one either way — but it has to be told, or it
  // defaults to labelling and the range reads "3:00 PM EST - 4:00 PM EST".
  const start = formatApptDateTime(startDate, {
    includeYear,
    timezone,
    showTimezone: false,
  });
  const end = endDate.toLocaleTimeString("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    timeZoneName: showTimezone ? "short" : undefined,
  });

  return `${start} - ${end}`;
};

/**
 * Formats a date range for display (e.g. "Saturday, January 31, 2026 at 11:00 AM - 12:00 PM EST").
 * The short timezone label is always appended — naming the zone is the point
 * of this formatter.
 */
export const formatApptDateTimeRangeWithTimezone = (
  startDate: Date,
  endDate: Date,
  timezone: string,
): string => {
  const start = formatApptDateTime(startDate, {
    long: true,
    includeYear: true,
    timezone,
    showTimezone: false,
  });
  const end = endDate.toLocaleTimeString("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    timeZoneName: "short",
  });
  return `${start} - ${end}`;
};

export const formatTimePickerValue = (date: Date, timezone: string): string => {
  return date.toLocaleTimeString("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  });
};

export const formatApptTime = (
  date: Date,
  opts: { timezone: string; showTimezone?: boolean; language?: Language },
): string => {
  const { timezone, showTimezone = true, language = Language.ENGLISH } = opts;
  return date.toLocaleTimeString(_localeFor(language), {
    timeZone: timezone,
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    timeZoneName: showTimezone ? "short" : undefined,
  });
};

export const formatTimelineDate = (date: Date, timezone: string) => {
  return DateTime.fromJSDate(date, { zone: timezone }).toFormat("MM.dd.yy");
};

/**
 * Formats a date object to YYYY-MM-DD.
 *
 * Takes the zone because the calendar day is zone-dependent: a 9pm Arizona
 * appointment is already the next day in Eastern, and this is the format we
 * submit as a claim's date of service.
 */
export const formatDateISO = (date: Date, timezone: string): string => {
  return DateTime.fromJSDate(date, { zone: timezone }).toFormat("yyyy-MM-dd");
};

export const formatDateMMDDYYYY = (
  date: Date,
  opts: { timezone: string },
): string => {
  const { timezone } = opts;
  return date.toLocaleDateString("en-US", {
    timeZone: timezone,
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

export const formatDateMMMDD = (
  date: Date,
  opts: { timezone: string },
): string => {
  const { timezone } = opts;
  return date.toLocaleDateString("en-US", {
    timeZone: timezone,
    month: "short",
    day: "numeric",
  });
};

/** Format a date as "Sat, Jan 10" */
export const formatShortDate = (date: Date, timezone: string): string => {
  return date.toLocaleDateString("en-US", {
    timeZone: timezone,
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

/**
 * "Tue Sep 29" — formatShortDate without the comma, for compact day headings
 * where the weekday reads as part of the label. Date-only, so unlabelled.
 */
export const formatWeekdayMonthDay = (date: Date, timezone: string): string =>
  DateTime.fromJSDate(date, { zone: timezone }).toFormat("EEE MMM d");

/**
 * "Sep 25, 2026" — the shape most administrative dates want, or
 * "Fri, Sep 25, 2026" with `weekday`.
 *
 * Date-only, so no zone label: which clock a bare calendar date was read on
 * is not something we surface (see modules/timezones/AGENTS.md, "Labelling").
 * The `timezone` is still required, because the calendar day itself differs
 * by zone.
 */
export const formatDateMedium = (
  date: Date,
  opts: { timezone: string; weekday?: boolean },
): string =>
  date.toLocaleDateString("en-US", {
    timeZone: opts.timezone,
    weekday: opts.weekday ? "short" : undefined,
    month: "short",
    day: "numeric",
    year: "numeric",
  });

/**
 * "September 2026" — month granularity, for the "working together since"
 * and "diagnosed in" lines that deliberately don't name a day.
 *
 * Still takes a zone, because a month boundary is a boundary: an instant a
 * few hours either side of midnight on the 1st falls in a different month
 * depending on the clock, and a relationship that began late on August 31st
 * reads as September to a reader further east.
 */
export const formatMonthYear = (
  date: Date,
  opts: { timezone: string },
): string =>
  date.toLocaleDateString("en-US", {
    timeZone: opts.timezone,
    month: "long",
    year: "numeric",
  });

/** "September 25, 2026". Date-only, so unlabelled — see formatDateMedium. */
export const formatDateLong = (
  date: Date,
  opts: { timezone: string },
): string =>
  date.toLocaleDateString("en-US", {
    timeZone: opts.timezone,
    month: "long",
    day: "numeric",
    year: "numeric",
  });

/**
 * "Sep 25, 2026 at 3:00 PM EDT" — a timestamp on something that happened,
 * as opposed to an appointment (which wants formatApptDateTime). With
 * `weekday`, "Fri, Sep 25, 2026 at 3:00 PM EDT".
 *
 * This one shows a time, so it labels by default. Pass `showTimezone: false`
 * only where the reader can be in one zone and one zone only.
 */
export const formatDateTimeMedium = (
  date: Date,
  opts: { timezone: string; showTimezone?: boolean; weekday?: boolean },
): string => {
  const { timezone, showTimezone = true, weekday = false } = opts;
  // Built from two calls rather than one so the separator is " at ", matching
  // formatApptDateTime. A single toLocaleString gives "Jun 15, 2026, 3:00 PM".
  const day = formatDateMedium(date, { timezone, weekday });
  const time = date.toLocaleTimeString("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
  });
  const formatted = `${day} at ${time}`;
  return showTimezone
    ? `${formatted} ${getShortTimezoneLabel(date, timezone)}`
    : formatted;
};

/**
 * "9/25/26 3:04 pm EDT" — the compact stamp a dense table cell wants, and
 * the read-back shape of the `M/d/yy h:mm aa` datetime picker.
 *
 * Shows a time, so it labels by default.
 */
export const formatDateTimeCompact = (
  date: Date,
  opts: { timezone: string; showTimezone?: boolean },
): string => {
  const { timezone, showTimezone = true } = opts;
  const day = date.toLocaleDateString("en-US", {
    timeZone: timezone,
    month: "numeric",
    day: "numeric",
    year: "2-digit",
  });
  const time = date
    .toLocaleTimeString("en-US", {
      timeZone: timezone,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();
  const formatted = `${day} ${time}`;
  return showTimezone
    ? `${formatted} ${getShortTimezoneLabel(date, timezone)}`
    : formatted;
};

/**
 * "October 8th". Takes the timezone the date should be read in, because the
 * calendar day differs by zone: an appointment near midnight Eastern falls on
 * the previous day in Arizona, and naming the wrong day to a family reads as
 * us having the wrong appointment.
 */
export const formatDateMMDDWithOrdinalSuffix = (
  date: Date,
  timezone: string,
): string => {
  const month = date.toLocaleDateString("en-US", {
    timeZone: timezone,
    month: "long",
  });
  const day = date.toLocaleDateString("en-US", {
    timeZone: timezone,
    day: "numeric",
  });
  return `${month} ${addOrdinalSuffix(Number(day))}`;
};

/**
 * The short zone label for `date` read in `timezone` — "EST", "MDT", "MST".
 *
 * Takes the date rather than reading `now`, because the label is itself
 * DST-dependent: a picker open in late October is in EDT today while the
 * November slots it is showing are EST, and a header reading "Times in EDT"
 * above EST times is the class of wrongness this module exists to remove.
 */
export const getShortTimezoneLabel = (date: Date, timezone: string): string =>
  new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    timeZoneName: "short",
  })
    .formatToParts(date)
    .find((part) => part.type === "timeZoneName")?.value ?? "";

/**
 * The zone's generic short label — "ET", "CT", "MST" for Phoenix — for a
 * wall-clock time that isn't one instant, such as "Mondays at 5:00 PM" over
 * weeks that straddle a DST change, where "EDT" or "EST" would each be wrong
 * for some of them.
 */
export const getGenericTimezoneLabel = (timezone: string): string =>
  new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    timeZoneName: "shortGeneric",
  })
    .formatToParts(new Date())
    .find((part) => part.type === "timeZoneName")?.value ?? "";

/**
 * How `secondary` sits relative to `primary` at `date` — "3 hours ahead",
 * "1 hour behind" — or null when the two show the same wall clock.
 *
 * Read at `date` rather than now, because the gap is not constant: Phoenix is
 * 3 hours behind Eastern in summer and 2 in winter, since Arizona doesn't
 * observe DST. A surface that computed this once from `now` would mislabel
 * every slot on the far side of a DST boundary.
 */
export const describeZoneOffset = (
  date: Date,
  primary: string,
  secondary: string,
): string | null => {
  const primaryOffset = DateTime.fromJSDate(date, { zone: primary }).offset;
  const secondaryOffset = DateTime.fromJSDate(date, { zone: secondary }).offset;
  const diffMinutes = secondaryOffset - primaryOffset;
  if (diffMinutes === 0) {
    return null;
  }

  const direction = diffMinutes > 0 ? "ahead" : "behind";
  const absMinutes = Math.abs(diffMinutes);
  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;

  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
  }
  return `${parts.join(" ")} ${direction}`;
};

/**
 * The same instant on a second clock — "4:00 PM EDT" — for a surface whose
 * primary zone belongs to the person acting rather than the person the time
 * is for: a therapist picking a slot their client has to keep.
 *
 * Null when both zones show the same wall clock, so the caller renders
 * nothing instead of "3:00 PM CST (3:00 PM CST)".
 */
export const formatTimeInSecondaryZone = (
  date: Date,
  primary: string,
  secondary: string,
): string | null => {
  if (describeZoneOffset(date, primary, secondary) === null) {
    return null;
  }
  const time = date.toLocaleTimeString("en-US", {
    timeZone: secondary,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${time} ${getShortTimezoneLabel(date, secondary)}`;
};

/**
 * Format a date as "4:00 pm on Oct 10" (or "4:00 pm EST on Oct 10" when a
 * timezone label is requested).
 */
export const formatTimeAndDate = (
  date: Date,
  opts: { timezone: string; showTimezone?: boolean },
): string => {
  const { timezone, showTimezone = true } = opts;
  const time = date
    .toLocaleTimeString("en-US", {
      timeZone: timezone,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();

  const dateStr = date.toLocaleDateString("en-US", {
    timeZone: timezone,
    month: "short",
    day: "numeric",
  });

  if (showTimezone) {
    return `${time} ${getShortTimezoneLabel(date, timezone)} on ${dateStr}`;
  }

  return `${time} on ${dateStr}`;
};

/**
 * Get the day of the week from a date object.
 *
 * @returns string - the day of the week (i.e. Monday or lunes)
 */
export const getDayOfWeek = (
  date: Date,
  language: Language = Language.ENGLISH,
  timezone: string,
): string => {
  return date.toLocaleDateString(_localeFor(language), {
    timeZone: timezone,
    weekday: "long",
  });
};

/**
 * Get time from a date object.
 *
 * @returns string - the time (i.e. 3:00 PM)
 */
export const getTime = (date: Date, timezone: string): string => {
  return date.toLocaleTimeString("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
};

export const addOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return day + "th";
  switch (day % 10) {
    case 1:
      return day + "st";
    case 2:
      return day + "nd";
    case 3:
      return day + "rd";
    default:
      return day + "th";
  }
};

/** Maps the Language enum to an Intl locale, falling back to en-US. */
const _localeFor = (language: Language): string => {
  if (language === Language.ENGLISH) {
    return DEFAULT_LOCALE;
  }
  // US Spanish so 12:00 stays 12:00 (not 0:00) and zone labels are EDT/MST.
  if (language === Language.SPANISH) {
    return "es-US";
  }
  return LanguageEnumToLocale[language] || DEFAULT_LOCALE;
};
